import { memo } from "react";

import {
  SOURCE_INLINE_LIMIT,
  formatSourceBytes,
  sourceName,
  type SourceCategoryStats,
} from "./source-explorer-model";
import type { ManagedSource, SourceRoleContract } from "./types";


export const SourceCategorySummary = memo(function SourceCategorySummary({
  role,
  sources,
  stats,
  explorerHref,
}: {
  role: SourceRoleContract;
  sources: ManagedSource[];
  stats: SourceCategoryStats;
  explorerHref: string;
}) {
  const systemProvided = role.providedBy === "system";
  const status = categoryStatus(role, stats);
  return <section className="source-category-summary" id={`source-role-${role.role}`} data-source-role={role.role}>
    <header>
      <div>
        <h3>{systemProvided ? `${role.label} — disponible` : role.label}</h3>
        <p className="source-category-metrics">{categoryMetrics(role, stats)}</p>
      </div>
      <span className={`dm-status source-category--${status.code}`}>{status.label}</span>
    </header>
    {systemProvided ? <p>{role.description || "Fourni par le système."}</p> : null}
    {!systemProvided && sources.length === 0 ? <p className="dm-empty">{role.required ? "Source manquante" : "Aucun fichier dans cette catégorie."}</p> : null}
    {!systemProvided && sources.length > 0 && sources.length <= SOURCE_INLINE_LIMIT ? <ul className="source-inline-files">
      {sources.map((source) => <li key={source.id}><strong>{sourceName(source)}</strong><span>{formatSourceBytes(Number(source.sizeBytes))} · {source.status} · {changeLabel(source)} · introduit dans r{source.introducedRevision ?? "?"}</span></li>)}
    </ul> : null}
    {role.analysisRequired && stats.fileCount && stats.analysis === "pending" ? <p className="dm-warning">{hasArchive(sources) ? "Archive présente — analyse requise" : "Sources présentes — analyse requise"}</p> : null}
    {!systemProvided && sources.length > SOURCE_INLINE_LIMIT ? <p className="source-collection-note">Les fichiers sont regroupés dans une vue détaillée pour conserver une page courte.</p> : null}
    <div className="source-collection-actions">
      <a className="btn btn-outline" href={explorerHref}>Ouvrir dans l’explorateur</a>
    </div>
  </section>;
});

function categoryStatus(role: SourceRoleContract, stats: SourceCategoryStats) {
  if (role.providedBy === "system") return { code: "available", label: "fourni par le système" };
  if (stats.invalidCount || stats.analysis === "invalid") return { code: "invalid", label: "invalide" };
  if (stats.analysis === "importing") return { code: "running", label: "import en cours" };
  if (stats.analysis === "running") return { code: "running", label: "analyse en cours" };
  if (role.analysisRequired && stats.fileCount && stats.analysis !== "succeeded") {
    return { code: "pending", label: "analyse requise" };
  }
  if (!stats.fileCount && role.required) return { code: "missing", label: "incomplet" };
  if (stats.analysis === "succeeded") return { code: "complete", label: "analyse réussie" };
  return { code: "complete", label: role.required ? "complet" : "optionnel" };
}

function categoryMetrics(role: SourceRoleContract, stats: SourceCategoryStats) {
  if (role.providedBy === "system") return "Disponible en lecture seule";
  const count = `${stats.fileCount} fichier${stats.fileCount > 1 ? "s" : ""}`;
  const values = [count, formatSourceBytes(stats.totalBytes), analysisText(role, stats)];
  if (stats.readyCount) values.push(role.analysisRequired ? `${stats.readyCount} valide(s)` : `${stats.readyCount} prêt(s)`);
  if (stats.invalidCount) values.push(`${stats.invalidCount} invalide(s)`);
  if (stats.pendingCount) values.push(`${stats.pendingCount} en attente`);
  return values.filter(Boolean).join(" · ");
}

function analysisText(role: SourceRoleContract, stats: SourceCategoryStats) {
  if (!role.analysisRequired) return "";
  if (stats.analysis === "importing") return "import en cours";
  if (stats.analysis === "succeeded") return "analyse réussie";
  if (stats.analysis === "invalid") return "analyse invalide";
  if (stats.analysis === "running") return stats.analysisProgress ? `analyse : ${stats.analysisProgress}` : "analyse en cours";
  if (stats.fileCount) return "analyse requise";
  return "analyse absente";
}

function hasArchive(sources: ManagedSource[]) {
  return sources.some((source) => sourceName(source).toLowerCase().endsWith(".zip"));
}

function changeLabel(source: ManagedSource) {
  if (source.changeType === "inherited") return "hérité";
  if (source.changeType === "replaced") return "remplacé";
  if (source.changeType === "moved") return "déplacé";
  return "ajouté";
}
